import find from 'lodash/find'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import { numberWithCommas, roundToPrecision } from 'utils/formatters'

import { EMPTY_CELL_CONTENT } from 'components/Core/Table/Cell'

import messages from './OrderTable.messages'

export const getOrdersSelected = ({
  orders = [],
  selectedOrdersIds = [],
  selectAll = false,
  allOrdersAreChecked = false,
}) => {
  if (selectAll && allOrdersAreChecked) {
    // If we select all, we only return orders not previously selected
    return orders.filter(({ id }) => !selectedOrdersIds.includes(id))
  }
  // Otherwise, all the input orders are affected
  return orders
}

export const hasToIncreaseDueTotalPrice = ({
  orders,
  selectedOrdersIds,
  selectAll,
  allOrdersAreChecked,
}) => {
  if (selectAll) {
    // If we are selecting/deselecting all, we increase/decrease
    return allOrdersAreChecked
  }
  // If we select a single order, we decrease if it is already selected. Otherwise, we increase.
  return (
    find(selectedOrdersIds, (id) => id === get(orders[0], 'id')) === undefined
  )
}

export const increaseDueTotalPrice = ({ priceAcc, currency, dueTotal }) => ({
  ...priceAcc,
  [currency]: {
    value: get(priceAcc[currency], 'value', 0) + dueTotal,
    numOrders: get(priceAcc[currency], 'numOrders', 0) + 1,
  },
})

export const decreaseDueTotalPrice = ({ priceAcc, currency, dueTotal }) => {
  const newPrice = roundToPrecision(priceAcc[currency].value - dueTotal, 2)
  const newNumOrders = priceAcc[currency].numOrders - 1

  if (newNumOrders === 0) {
    const { [currency]: _, ...priceAccWithoutCode } = priceAcc
    return priceAccWithoutCode
  }
  return {
    ...priceAcc,
    [currency]: {
      value: newPrice,
      numOrders: newNumOrders,
    },
  }
}

export const updateDueTotalPriceByCurrency = ({
  initialPriceAcc,
  orders,
  selectedOrdersIds,
  selectAll,
  allOrdersAreChecked,
}) => {
  // Fix for a border case when the user has selected rows,
  // but we don't have data of the selected rows
  if (!!selectedOrdersIds?.length && !Object.keys(initialPriceAcc).length) {
    return initialPriceAcc
  }

  const ordersSelected = getOrdersSelected({
    orders,
    selectedOrdersIds,
    selectAll,
    allOrdersAreChecked,
  })
  const hasToIncreaseValue = hasToIncreaseDueTotalPrice({
    orders,
    selectedOrdersIds,
    selectAll,
    allOrdersAreChecked,
  })

  return ordersSelected.reduce((priceAcc, order) => {
    const currency = get(order, 'priceType.currency.code', EMPTY_CELL_CONTENT)
    const dueTotal = parseFloat(
      get(order, 'financials.dueTotal.maxPrice', '0.00'),
    )

    if (hasToIncreaseValue) {
      return increaseDueTotalPrice({
        priceAcc,
        currency,
        dueTotal,
      })
    }

    return decreaseDueTotalPrice({ priceAcc, currency, dueTotal })
  }, initialPriceAcc)
}

export const getFilteredOrders = ({ orders = [], selectedOrdersIds = [] }) => {
  if (isEmpty(selectedOrdersIds)) {
    return []
  }
  return orders.filter(({ id }) => selectedOrdersIds.includes(id))
}

export const getOrderTotalWithCurrency = (order, total) => {
  const currency = get(order, 'priceType.currency.code')
  return currency && total ? `${numberWithCommas(total)} ${currency}` : null
}

export const getStatusMapping = () => ({
  CANCELLED: messages.statusCancelled,
  APPROVED: messages.statusApproved,
  ALL_NOTES: messages.statusNotes,
  IN_PROGRESS: messages.statusInProgress,
  PENDING: messages.statusPending,
  SHIPPED: messages.statusShipped,
  MULTIPLE: messages.multipleStatuses,
  FAILED: messages.statusFailed,
  SCHEDULED: messages.statusScheduled,
  SUCCESS: messages.statusSuccess,
  NONE: messages.statusNone,
})
